import { useCallback, useEffect, useRef, useState } from 'react';

// Any of these resets the idle clock. `wheel` catches scrolling a long menu; the
// rest cover touch and physical input.
const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'touchstart', 'wheel'];

/**
 * The safety net behind the header Lock button: after `timeoutMs` with no input on
 * a shared FOH terminal, run `onLock` (which drops back to the PIN screen). The last
 * `warnMs` of that window is surfaced as `secondsLeft` so the UI can warn before it
 * happens — any touch cancels it.
 *
 * Drafts make this non-destructive: the cart is already mirrored to localStorage, so
 * whoever picks the terminal back up gets the in-progress order.
 */
/**
 * What the idle clock does on this tick. Pure, so the rule is tested without a browser.
 *
 * `held` is a kitchen screen. A board on the wall goes untouched for minutes between
 * tickets, and locking it to a PIN pad mid-service hides new tickets from a chef in gloves.
 * So while held the lock never fires and never warns. Whether a touch there extends the
 * session depends on whose board it is (see `extendsSession`): for someone who works it, a
 * touch is use like any other; for anyone else the deadline keeps running from their last
 * touch anywhere *else*, and leaving the board, the very next tick checks it as normal. A
 * server who glanced at the board for half a minute walks back in; one who left it signed in
 * at five cannot be used at nine.
 */
export const idleStep = ({ now, deadline, held, warnMs }) => {
  const remaining = deadline - now;
  if (held) return { action: 'held' };
  if (remaining <= 0) return { action: 'lock' };
  if (remaining <= warnMs) return { action: 'warn', secondsLeft: Math.ceil(remaining / 1000) };
  return { action: 'idle' };
};

/** Does a touch here push the deadline out? Anywhere but a held screen, yes; on a held screen
 *  only for someone who works it (`works`: the kitchen on the kitchen screen). The tap that
 *  takes a chef off the board is a touch on the board, so it used to count for nothing: after
 *  a minute on the board, leaving it locked the till at once (Martin, 31 Oct). */
export const extendsSession = (held, works = false) => !held || works;

// A minute (Oct 2026 sweep; it was 90 seconds): a shared till left signed in is someone else's
// orders, voids and cash under your name.
export function useIdleLock(onLock, { timeoutMs = 60_000, warnMs = 15_000, hold = false, works = false } = {}) {
  const [secondsLeft, setSecondsLeft] = useState(null); // null unless inside the warn window
  const onLockRef = useRef(onLock);
  onLockRef.current = onLock;
  const holdRef = useRef(hold);
  holdRef.current = hold;
  const worksRef = useRef(works);
  worksRef.current = works;
  const deadlineRef = useRef(Date.now() + timeoutMs);

  const bump = useCallback(() => {
    if (!extendsSession(holdRef.current, worksRef.current)) return;
    deadlineRef.current = Date.now() + timeoutMs;
    // Same-value setState is a no-op in React, so taps outside the warn window
    // don't cause re-renders.
    setSecondsLeft((s) => (s === null ? s : null));
  }, [timeoutMs]);

  useEffect(() => {
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, bump, { passive: true }));
    const onVisible = () => { if (!document.hidden) bump(); };
    document.addEventListener('visibilitychange', onVisible);

    const tick = setInterval(() => {
      const step = idleStep({ now: Date.now(), deadline: deadlineRef.current, held: holdRef.current, warnMs });
      if (step.action === 'lock') {
        // Push the deadline out so we don't re-fire on the next tick before unmount.
        deadlineRef.current = Date.now() + timeoutMs;
        setSecondsLeft(null);
        onLockRef.current?.();
      } else if (step.action === 'warn') {
        setSecondsLeft(step.secondsLeft);
      } else {
        setSecondsLeft((s) => (s === null ? s : null));
      }
    }, 1000);

    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, bump));
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(tick);
    };
  }, [bump, timeoutMs, warnMs]);

  return { secondsLeft };
}

export default useIdleLock;
